import { useTranslation } from 'react-i18next';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '~/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '~/components/ui/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '~/components/ui/tooltip';
import { cn } from '~/utils/cn';

import {
  useFieldMatrixSummary,
  useFieldSetsNotReportedNote,
} from '../hooks/use-field-matrix-summary';
import { useRunLabels } from '../hooks/use-run-labels';
import type { FieldSets } from '../types/run-detail.api.types';
import { fieldIconOf } from '../utils/field-icons';
import {
  CELL_ICON,
  type CellState,
  matrixRow,
  type MatrixRow,
  sendsBasisOf,
  sendsKeySuffix,
} from '../utils/field-matrix';
import { fieldSetFields } from '../utils/field-plan';
import { DASH, formatCount } from '../utils/run-format';

import { ConnectorMark } from './connector-mark';
import { HintTerm } from './hint-term';

/** Column widths (shared by header and body via the colgroup): the four ✓/—/⚠ mark columns are
 *  equal; Field takes whatever is left. Tuned so the product view fits max-w-5xl without scroll. */
const MARK_COL = 'w-[6.5rem]';
const PRODUCTS_COL = 'w-[5.5rem]';
const THIS_PRODUCT_COL = 'w-[7rem]';
/** One header style (single line) and one body padding/alignment for every column. */
const HEAD = 'h-9 px-3 text-xs font-medium whitespace-nowrap';
const CELL = 'px-3 py-2.5 align-middle';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  data: FieldSets;
  /** Products content generation handled — the Products column's "20/20" denominator; omitted →
   *  the Products column shows the written count alone. */
  productsTotal?: number | null;
  /** Per-product view: whether THIS product got content for a field code — adds a "This product"
   *  column (✓ / —). Omitted on the batch-level matrix. */
  productWritten?: ((code: string) => boolean) | null;
}

/**
 * The field matrix (2026-10-16): Field · Asked · Allowed · Sends · Written · Products, a sticky
 * one-line header, fixed column widths (header and body aligned per column), amber rows for fields
 * on the delivery list no product got content for, and one short note chip per noted row. ONE
 * implementation shared by the batch's "What ships" card and the product page (which adds a "This
 * product" column) — never a second matrix.
 */
export function FieldMatrixDialog({
  open,
  onOpenChange,
  data,
  productsTotal = null,
  productWritten = null,
}: Props) {
  const { t } = useTranslation('catalog-enrichment-run');
  const { fieldLabel } = useRunLabels();

  const fields = fieldSetFields(data);
  const rows = fields.map((field) => matrixRow(data, field));
  const matrixSummary = useFieldMatrixSummary(data);
  const notReportedNote = useFieldSetsNotReportedNote(data);
  // §90 — "Requested" until integrations confirms the push, "Delivered" after; today's "Sends"
  // on an older backend. The hint says a push is confirmed, never a field.
  const sendsSuffix = sendsKeySuffix(sendsBasisOf(data));
  const legendStates: CellState[] = ['yes', 'no', 'problem', 'alwaysOn'];
  if (rows.some((r) => r.accepts === 'unrestricted')) legendStates.push('unrestricted');
  const columns: {
    key: keyof Pick<MatrixRow, 'asked' | 'accepts' | 'sent' | 'written'>;
    label: string;
    hint: string;
  }[] = [
    {
      key: 'asked',
      label: t('fieldSets.matrix.colShort.asked'),
      hint: t('fieldSets.matrix.colHint.asked'),
    },
    {
      key: 'accepts',
      label: t('fieldSets.matrix.colShort.accepts'),
      hint: t('fieldSets.matrix.colHint.accepts'),
    },
    {
      key: 'sent',
      label: t(`fieldSets.matrix.colShort.sent${sendsSuffix}`),
      hint: t(`fieldSets.matrix.colHint.sent${sendsSuffix}`),
    },
    {
      key: 'written',
      label: t('fieldSets.matrix.colShort.written'),
      hint: t('fieldSets.matrix.colHint.written'),
    },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[85vh] max-w-5xl flex-col gap-3">
        <DialogHeader className="pr-8">
          {/* Title + summary on ONE baseline-aligned line ("What ships to [G] · 6 fields · 4 to
              deliver · …") so the reader gets the totals before the table. */}
          <div className="flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5">
            <DialogTitle className="flex items-center gap-1.5">
              {t('fieldSets.matrix.titleTo')}
              <ConnectorMark code={data.connectorCode} />
            </DialogTitle>
            <DialogDescription className="text-sm">
              · {t('fieldSets.summaryFields', { count: fields.length })}
              {matrixSummary ? ` · ${matrixSummary}` : ''}
            </DialogDescription>
          </div>
          {notReportedNote ? (
            <p className="text-muted-foreground text-xs">{notReportedNote}</p>
          ) : null}
        </DialogHeader>
        <div className="min-h-0 overflow-hidden rounded-lg border">
          {/* table-fixed + a colgroup: every column has ONE width for header and body alike, so
              marks line up under their header and nothing scrolls sideways at ≥1024px. */}
          <Table containerClassName="max-h-[60vh]" className="min-w-[44rem] table-fixed">
            <colgroup>
              <col />
              {columns.map((col) => (
                <col key={col.key} className={MARK_COL} />
              ))}
              <col className={PRODUCTS_COL} />
              {productWritten ? <col className={THIS_PRODUCT_COL} /> : null}
            </colgroup>
            <TableHeader className="bg-background sticky top-0 z-10">
              <TableRow className="hover:bg-transparent">
                <TableHead className={cn(HEAD, 'text-left')}>
                  {t('fieldSets.matrix.col.field')}
                </TableHead>
                {columns.map((col) => (
                  <TableHead key={col.key} className={cn(HEAD, 'text-center')}>
                    <HintTerm hint={col.hint}>{col.label}</HintTerm>
                  </TableHead>
                ))}
                <TableHead className={cn(HEAD, 'text-right')}>
                  <HintTerm hint={t('fieldSets.matrix.colHint.products')}>
                    {t('fieldSets.matrix.colShort.products')}
                  </HintTerm>
                </TableHead>
                {productWritten ? (
                  <TableHead className={cn(HEAD, 'text-center')}>
                    <HintTerm hint={t('fieldSets.matrix.colHint.thisProduct')}>
                      {t('fieldSets.matrix.colShort.thisProduct')}
                    </HintTerm>
                  </TableHead>
                ) : null}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => {
                const FieldIcon = fieldIconOf(row.field);
                const muted =
                  row.note === 'writtenNotSent' ||
                  row.note === 'writtenNotRequested' ||
                  row.note === 'dropped';
                const productState: CellState | null = productWritten
                  ? productWritten(row.field)
                    ? 'yes'
                    : 'no'
                  : null;
                const productIcon = productState ? CELL_ICON[productState] : null;
                const label = fieldLabel(row.field);
                return (
                  <TableRow
                    key={row.field}
                    className={cn(row.note === 'attention' && 'bg-warning-50 hover:bg-warning-50')}
                  >
                    <TableCell className={CELL}>
                      {/* Fixed 1rem icon column + truncating name; the note chip sits in the
                          name's column on line 2, so it starts exactly under the name. */}
                      <span className="grid min-w-0 grid-cols-[1rem_minmax(0,1fr)] items-center gap-x-2 gap-y-1">
                        <FieldIcon aria-hidden="true" className="text-muted-foreground size-4" />
                        <span
                          className={cn(
                            'min-w-0 truncate font-medium',
                            muted ? 'text-muted-foreground' : 'text-foreground',
                          )}
                          title={`${label} (${row.field})`}
                        >
                          {label}
                        </span>
                        {row.note ? <NoteChip note={row.note} /> : null}
                      </span>
                    </TableCell>
                    {columns.map((col) => {
                      const state = row[col.key];
                      const { Icon, tone } = CELL_ICON[state];
                      // U6 — Written not reported (`enriched: null`): a plain "—", never a glyph
                      // that could read as "no" / 0; the note above says why.
                      if (col.key === 'written' && state === 'unknown') {
                        return (
                          <TableCell
                            key={col.key}
                            className={cn(CELL, 'text-muted-foreground text-center')}
                            title={t('fieldSets.matrix.cell.unknown')}
                          >
                            {DASH}
                            <span className="sr-only">{t('fieldSets.matrix.cell.unknown')}</span>
                          </TableCell>
                        );
                      }
                      const showCount =
                        col.key === 'written' && state === 'problem' && row.coverage !== null;
                      return (
                        <TableCell
                          key={col.key}
                          className={cn(CELL, 'text-center')}
                          // "No restriction requested" is not a check that passed — say so on hover.
                          title={
                            state === 'unrestricted'
                              ? t('fieldSets.matrix.cellHint.unrestricted')
                              : undefined
                          }
                        >
                          {/* The count hangs off the glyph's right edge (absolute), so the glyph
                              itself stays centred under its header like every other row's. */}
                          <span className="relative inline-flex items-center align-middle">
                            <Icon aria-hidden="true" className={cn('size-4', tone)} />
                            {showCount ? (
                              <span className="text-warning absolute left-full ml-1 text-xs tabular-nums">
                                {formatCount(row.coverage)}
                              </span>
                            ) : null}
                            <span className="sr-only">{t(`fieldSets.matrix.cell.${state}`)}</span>
                          </span>
                        </TableCell>
                      );
                    })}
                    <TableCell
                      className={cn(
                        CELL,
                        'text-muted-foreground text-right text-xs whitespace-nowrap tabular-nums',
                      )}
                    >
                      {row.coverage === null
                        ? DASH
                        : productsTotal
                          ? `${formatCount(row.coverage)}/${formatCount(productsTotal)}`
                          : formatCount(row.coverage)}
                    </TableCell>
                    {productState && productIcon ? (
                      <TableCell className={cn(CELL, 'text-center')}>
                        <span className="inline-flex items-center align-middle">
                          <productIcon.Icon
                            aria-hidden="true"
                            className={cn('size-4', productIcon.tone)}
                          />
                          <span className="sr-only">
                            {t(`fieldSets.matrix.cell.${productState}`)}
                          </span>
                        </span>
                      </TableCell>
                    ) : null}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
        <p className="text-muted-foreground flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
          {legendStates.map((state) => {
            const { Icon, tone } = CELL_ICON[state];
            return (
              <span key={state} className="inline-flex items-center gap-1">
                <Icon aria-hidden="true" className={cn('size-3.5', tone)} />
                {t(`fieldSets.matrix.cell.${state}`)}
              </span>
            );
          })}
          <span>{t('fieldSets.matrix.headerHelp')}</span>
        </p>
      </DialogContent>
    </Dialog>
  );
}

/** A row's note as ONE short chip ("Asked, not written") — the full sentence lives in its
 *  tooltip (and for screen readers), so no row ever wraps into a multi-line paragraph. */
function NoteChip({ note }: { note: NonNullable<MatrixRow['note']> }) {
  const { t } = useTranslation('catalog-enrichment-run');
  const full = t(`fieldSets.matrix.note.${note}`);
  return (
    <Tooltip delayDuration={200}>
      <TooltipTrigger asChild>
        <span
          className={cn(
            'col-start-2 w-fit max-w-full cursor-help truncate rounded-full border px-2 py-px text-xs',
            note === 'attention'
              ? 'border-warning/30 bg-background text-warning'
              : 'bg-muted text-muted-foreground border-transparent',
          )}
        >
          {t(`fieldSets.matrix.noteShort.${note}`)}
          <span className="sr-only">: {full}</span>
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">{full}</TooltipContent>
    </Tooltip>
  );
}
